import { ChangeDetectorRef, Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Header, Role } from '@aptsi-types';
import { Subject } from 'rxjs';
import { TimeFormatService } from 'src/app/features/ada-access/shared/service/time-format.service';
import { UserService } from '../../users/user.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DeleteDialogComponent } from '../../shared/dialogs/delete-dialog/delete-dialog.component';
import { RoleService } from '../role.service';
import { KeycloakStateService } from 'src/app/core/service/utility.service';

@Component({
  selector: 'app-role-search',
  templateUrl: './role-search.component.html',
  styleUrls: ['./role-search.component.scss']
})
export class RoleSearchComponent {
  isCreateMode = false;
  isUpdateMode = false;
  selectedUserId: string | null = null;

  // ─── Pagination ───────────────────────────────────────────────
  currentPage = 1;
  itemsPerPage = 7;
  totalPages = 0;
  totalPagesNormal = 0;
  totalPagesSearch = 0;

  // ─── Search & Filtering ──────────────────────────────────────
  searchText: string = null;
  filterByHeader: string = null;
  isSearch = false;
  searchTextChanged = new Subject<string>();
  organizationId: string;

  // ─── Data ─────────────────────────────────────────────────────
  role: Role[] = [];
  filteredData: Role[] = [];
  searchedData: Role[] = [];

    buttonConfig = {
  update: false,
  delete: false,
  import: false,
  configure: false
};

  // ─── UI Configuration ─────────────────────────────────────────
  headers: Header[] = [
    {
      name: "Role Id",
      property: "principalTypeId",
      showInDropdown: true,
      description: "Unique identifier for the user",
    },
    {
      name: "Role Name",
      property: "name",
      showInDropdown: true,
      description: "First name of the user",
    },

  ];



  constructor(
    private router: Router,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private timeFormatService: TimeFormatService,
    private roleService: RoleService,
    private keycloakStateService: KeycloakStateService
  ) { }

  // ──────────────────────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────────────────────
  async ngOnInit(): Promise<void> {
       const dropdownHeaders = this.headers.filter(
      header => header.showInDropdown
    );

    if (dropdownHeaders.length > 1) {
      this.filterByHeader = dropdownHeaders[1].property;
    }

    this.organizationId = this.keycloakStateService.getOrganizationId();
    await this.fetchRoles();

    this.searchTextChanged
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((text) => {
        this.searchText = text;
        this.searchRoles();
      });

    this.cdr.detectChanges();

  }



  // ──────────────────────────────────────────────────────────────
  // Getters
  // ──────────────────────────────────────────────────────────────
  get paginatedData(): Role[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.isSearch
      ? this.searchedData.slice(start, end)
      : this.filteredData.slice(start, end);
  }

  // ──────────────────────────────────────────────────────────────
  // Event Handlers
  // ──────────────────────────────────────────────────────────────
  changePage(page: number): void {
    this.currentPage = page;
    this.totalPages = this.isSearch
      ? this.totalPagesSearch
      : this.totalPagesNormal;
    this.isSearch ? this.fetchRoleSearch() : this.fetchRoles();
  }

  onSearchTextChange(searchValue: string): void {
    this.searchTextChanged.next(searchValue);
  }



  // ──────────────────────────────────────────────────────────────
  // Navigation
  // ──────────────────────────────────────────────────────────────


  closeForm(): void {
    this.isCreateMode = false;
    this.isUpdateMode = false;
    this.selectedUserId = null;
  }


  back(): void {
    if (this.isSearch) {
      this.isSearch = false;
      this.fetchRoles();
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Data Fetching
  // ──────────────────────────────────────────────────────────────
  async fetchRoles(): Promise<void> {
    try {
      console.log("organization Id in fetch role:", this.organizationId)
      const response = await this.roleService
        .getRoles(this.currentPage - 1, this.organizationId)
        .toPromise();
      this.formatAndSetRoles(response, false);
    } catch (error) {
      console.error("Error fetching rules:", error);
    }
  }

  async fetchRoleSearch(): Promise<void> {
    try {
      const response = await this.roleService
        .getRoleSearch(
          this.currentPage - 1,
          this.searchText,
          this.filterByHeader,
          this.organizationId
        )
        .toPromise();
      this.formatAndSetRoles(response, true);
    } catch (error) {
      console.error("Error fetching user search:", error);
    }
  }

  private formatAndSetRoles(response: any[], isSearch: boolean): void {
    const formatted = response.map((user) => ({
      ...user,
      timestamp: this.timeFormatService.formatDate(user.timestamp),
    }));

    if (isSearch) {
      this.searchedData = formatted;
      this.totalPagesSearch = response[0]?.totalPages || 0;
    } else {
      this.filteredData = formatted;
      this.totalPagesNormal = response[0]?.totalPages || 0;
    }

    this.updatePagination(response);
  }

  private updatePagination(response: any[]): void {
    if (response.length > 0 && response[0].totalPages) {
      const totalPages = response[0].totalPages;
      const currentPage = response[0].currentPage + 1;

      if (this.isSearch) {
        this.totalPagesSearch = totalPages;
      } else {
        this.totalPagesNormal = totalPages;
      }

      this.totalPages = totalPages;
      this.currentPage = currentPage;
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Search
  // ──────────────────────────────────────────────────────────────
  // async searchRoles(): Promise<void> {
  //   try {
  //     if (this.searchText) {
  //       this.isSearch = true;

  //       await this.fetchRoleSearch();
  //       this.totalPages = this.totalPagesSearch;

  //       const selectedHeader = this.headers.find(
  //         (h) => h.property === this.filterByHeader && h.showInDropdown
  //       );
  //       if (selectedHeader) {
  //         this.searchedData = this.searchedData.filter((role) => {
  //           const value = role[selectedHeader.property];
  //           return value
  //             ?.toString()
  //             .toLowerCase()
  //             .includes(this.searchText.toLowerCase());
  //         });

  //         this.searchedData.sort((a, b) =>
  //           a[selectedHeader.property]
  //             ?.toString()
  //             .localeCompare(b[selectedHeader.property]?.toString())
  //         );
  //       } else {
  //         this.filteredData = [];
  //       }
  //     } else {
  //       this.isSearch = false;

  //       await this.fetchRoles();
  //     }
  //   } catch (error) {
  //     console.error("Error during search:", error);
  //   }
  // }

    async searchRoles(): Promise<void> {

    try {

      if (this.searchText?.trim()) {

        this.isSearch = true;
        this.currentPage = 1;

        if (!this.filterByHeader) {
          this.filterByHeader = 'all';
        }

        // Backend handles:
        // search
        // pagination
        // filtering
        await this.fetchRoleSearch();

      } else {

        this.isSearch = false;
        this.currentPage = 1;

        await this.fetchRoles();
      }

    } catch (error) {

      console.error(
        'Error during search:',
        error
      );
    }
  }



}